import { Button } from '@talentmesh/ui/components/button';
import { Label } from '@talentmesh/ui/components/label';
import { RHFInput } from '@talentmesh/ui/components/rhf-input';
import { Spinner } from '@talentmesh/ui/components/spinner';
import { FormProvider } from 'react-hook-form';
import LinkedInMessageEditor from './LinkedInMessageEditor';
import { useLinkedInMessageForm } from './useLinkedInMessageForm';
import TMWingsAnimation from '@/Components/Animations/TMWingsAnimation/TMWingsAnimation';
import TypingTextAnimation from '@/Components/Animations/TypingTextAnimation';
import {
    useLinkedInMessageOutreach,
    useSaveLinkedInMessageOutreach,
} from '@/Pages/Candidates/AiSearch/api/useOutreachCandidate';
import htmlToPlainText from '@/Pages/Candidates/AiSearch/utils/htmlToPlainText';
import UIStrings from '@/Utils/UIStrings';

interface LinkedInMessageContainerProps {
    onClose?: (isMessageCopied?: boolean) => void;
}

const LinkedInMessageContainer = ({ onClose }: LinkedInMessageContainerProps) => {
    const form = useLinkedInMessageForm();
    const { trigger: saveLinkedInMessage } = useSaveLinkedInMessageOutreach();
    const { mutate: mutateSavedMessage } = useLinkedInMessageOutreach();

    const handleCopyMessage = form.handleSubmit(async ({ messageHtml, subject }) => {
        try {
            await saveLinkedInMessage({ message: messageHtml, subject });
            mutateSavedMessage();
        } finally {
            const plainText = htmlToPlainText(messageHtml || '');

            const clipboardItem = new ClipboardItem({
                'text/html': new Blob([messageHtml], { type: 'text/html' }),
                'text/plain': new Blob([plainText], { type: 'text/plain' }),
            });

            navigator.clipboard.write([clipboardItem]);

            onClose?.(true);
        }
    });

    return (
        <div className="w-full">
            <FormProvider {...form}>
                <form onSubmit={handleCopyMessage} className="flex flex-col gap-6">
                    <div className="flex flex-col gap-2">
                        <Label htmlFor="subject">{UIStrings.Subject}</Label>
                        {form.formState.isLoading ? (
                            <div className="bg-background border-input flex h-10 w-full items-center gap-1 rounded-xl border p-2">
                                <TMWingsAnimation width="28px" height="28px" />
                                <TypingTextAnimation deletionSpeed={20} texts={[UIStrings.GeneratingSubjectWithAI]} />
                            </div>
                        ) : (
                            <RHFInput
                                placeholder={UIStrings.AddSubject}
                                className="w-full"
                                {...form.register('subject')}
                            />
                        )}
                    </div>
                    <div className="bg-background flex h-115 max-h-115 flex-col">
                        {form.formState.isLoading ? (
                            <div className="border-input flex h-full w-full flex-col items-center justify-center gap-4 rounded-md border">
                                <h2 className="text-2xl font-semibold">{UIStrings.DraftingYourMessage}</h2>
                                <TMWingsAnimation className="h-42" />
                                <TypingTextAnimation
                                    deletionSpeed={20}
                                    texts={[
                                        UIStrings.AIIsPreparingTheFirstDraft,
                                        UIStrings.DraftingYourLinkedInInMailMessage,
                                    ]}
                                />
                            </div>
                        ) : (
                            <LinkedInMessageEditor />
                        )}
                    </div>
                    <Button
                        type="submit"
                        size="lg"
                        className="w-fit self-end"
                        disabled={form.formState.disabled || form.formState.isLoading || form.formState.isSubmitting}
                    >
                        {form.formState.isSubmitting && <Spinner className="size-4" />}
                        {UIStrings.CopyMessage}
                    </Button>
                </form>
            </FormProvider>
        </div>
    );
};

export default LinkedInMessageContainer;
